/**
 * 自治体組織設定サービス（統合版）
 * 本番API、モックAPI、環境切り替えを一つのファイルで管理
 * 作業方針 8.1 自治体組織設定の仕様に準拠
 */

import { USE_MOCK_API, API_BASE_URL, IS_DEVELOPMENT } from '../config.js'

// =============================================================================
// TypeScript型定義（JSDoc形式）
// =============================================================================

/**
 * @typedef {Object} MunicipalOrganization
 * @property {number} id - 組織ID
 * @property {string} organizationName - 組織名（最大20文字）
 * @property {string} createdAt - 作成日時（ISO 8601）
 * @property {string} updatedAt - 更新日時（ISO 8601）
 */

/**
 * @typedef {Object} MunicipalOrganizationRequest
 * @property {string} organizationName - 必須、1-20文字、重複不可
 */

/**
 * @typedef {Object} ErrorResponse
 * @property {string} error - エラータイプ
 * @property {string} message - エラーメッセージ
 * @property {number} statusCode - HTTPステータスコード
 */

// バリデーション定数
const VALIDATION_RULES = {
  ORGANIZATION_NAME_MAX_LENGTH: 20,
  ORGANIZATION_NAME_MIN_LENGTH: 1
}

// =============================================================================
// モックデータとAPI
// =============================================================================

// モックデータ
let mockOrganizations = [
  {
    id: 1,
    organizationName: '廃棄物管理課',
    createdAt: '2024-01-15T09:00:00.000Z',
    updatedAt: '2024-01-15T09:00:00.000Z'
  },
  {
    id: 2,
    organizationName: '清掃事業所',
    createdAt: '2024-01-20T10:30:00.000Z',
    updatedAt: '2024-01-20T10:30:00.000Z'
  },
  {
    id: 3,
    organizationName: '環境政策課',
    createdAt: '2024-02-01T14:15:00.000Z',
    updatedAt: '2024-02-01T14:15:00.000Z'
  },
  {
    id: 4,
    organizationName: '環境衛生課',
    createdAt: '2024-02-10T16:45:00.000Z',
    updatedAt: '2024-02-10T16:45:00.000Z'
  },
  {
    id: 5,
    organizationName: 'リサイクル推進課',
    createdAt: '2024-03-01T11:20:00.000Z',
    updatedAt: '2024-03-01T11:20:00.000Z'
  }
]

// モック用ユーティリティ
const simulateDelay = (ms = 800) => new Promise(resolve => setTimeout(resolve, ms))
const shouldSimulateError = () => IS_DEVELOPMENT && Math.random() < 0.01

// =============================================================================
// モックAPI実装
// =============================================================================

const mockApi = {
  async getMunicipalOrganizations() {
    await simulateDelay()

    if (shouldSimulateError()) {
      throw new Error('ネットワークエラーが発生しました（モック）')
    }

    return {
      success: true,
      data: [...mockOrganizations],
      total: mockOrganizations.length,
      timestamp: new Date().toISOString()
    }
  },

  async createMunicipalOrganization(organizationData) {
    await simulateDelay()

    // 重複チェック
    const isDuplicate = mockOrganizations.some(org =>
      org.organizationName === organizationData.organizationName
    )

    if (isDuplicate) {
      throw new Error('この組織名は既に登録されています')
    }

    if (shouldSimulateError()) {
      throw new Error('サーバーエラーが発生しました（モック）')
    }

    // 新しい組織を作成
    const newOrganization = {
      id: Math.max(...mockOrganizations.map(org => org.id)) + 1,
      organizationName: organizationData.organizationName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    mockOrganizations.push(newOrganization)

    return {
      success: true,
      data: newOrganization,
      message: '自治体組織が正常に作成されました'
    }
  },

  async updateMunicipalOrganization(id, organizationData) {
    await simulateDelay()

    const orgId = parseInt(id)
    const orgIndex = mockOrganizations.findIndex(org => org.id === orgId)

    if (orgIndex === -1) {
      throw new Error('指定された組織が見つかりません')
    }

    // 重複チェック（編集対象以外との重複）
    const isDuplicate = mockOrganizations.some(org =>
      org.id !== orgId && org.organizationName === organizationData.organizationName
    )

    if (isDuplicate) {
      throw new Error('この組織名は既に登録されています')
    }

    if (shouldSimulateError()) {
      throw new Error('サーバーエラーが発生しました（モック）')
    }

    // 組織を更新
    const updatedOrganization = {
      ...mockOrganizations[orgIndex],
      organizationName: organizationData.organizationName,
      updatedAt: new Date().toISOString()
    }

    mockOrganizations[orgIndex] = updatedOrganization

    return {
      success: true,
      data: updatedOrganization,
      message: '自治体組織が正常に更新されました'
    }
  },

  async deleteMunicipalOrganization(id) {
    await simulateDelay()

    const orgId = parseInt(id)
    const orgIndex = mockOrganizations.findIndex(org => org.id === orgId)

    if (orgIndex === -1) {
      throw new Error('指定された組織が見つかりません')
    }

    if (shouldSimulateError()) {
      throw new Error('サーバーエラーが発生しました（モック）')
    }


    const deletedOrganization = mockOrganizations.splice(orgIndex, 1)[0]

    return {
      success: true,
      data: deletedOrganization,
      message: '自治体組織が正常に削除されました'
    }
  }
}

// =============================================================================
// 本番API実装
// =============================================================================

const productionApi = {
  async getMunicipalOrganizations() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/municipal-organizations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // TODO: 認証ヘッダーを追加
          // 'Authorization': `Bearer ${getAuthToken()}`
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error(`HTTP error! status: ${response.status}, body: ${errorText}`)

        switch (response.status) {
          case 401:
            throw new Error('認証が必要です。再度ログインしてください。')
          case 403:
            throw new Error('この操作を実行する権限がありません。')
          case 500:
            throw new Error('サーバーエラーが発生しました。システム管理者にお問い合わせください。')
          default:
            throw new Error(`HTTP error! status: ${response.status}`)
        }
      }

      const data = await response.json()

      // 作業方針 8.1 の形式に準拠
      if (Array.isArray(data)) {
        return data
      }

      return data.data || data
    } catch (error) {
      console.error('Failed to fetch municipal organizations:', error)

      if (error.message.includes('認証') || error.message.includes('権限') || error.message.includes('サーバーエラー')) {
        throw error
      }

      throw new Error('自治体組織一覧の取得に失敗しました')
    }
  },

  async createMunicipalOrganization(organizationData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/municipal-organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // TODO: 認証ヘッダーを追加
        },
        body: JSON.stringify(organizationData)
      })

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('この組織名は既に登録されています')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to create municipal organization:', error)
      if (error.message.includes('既に登録されています')) {
        throw error
      }
      throw new Error('自治体組織の作成に失敗しました')
    }
  },

  async updateMunicipalOrganization(id, organizationData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/municipal-organizations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // TODO: 認証ヘッダーを追加
        },
        body: JSON.stringify(organizationData)
      })

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('この組織名は既に登録されています')
        }
        if (response.status === 404) {
          throw new Error('指定された組織が見つかりません')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to update municipal organization:', error)
      if (error.message.includes('既に登録されています') || error.message.includes('見つかりません')) {
        throw error
      }
      throw new Error('自治体組織の更新に失敗しました')
    }
  },

  async deleteMunicipalOrganization(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/municipal-organizations/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // TODO: 認証ヘッダーを追加
        }
      })

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('指定された組織が見つかりません')
        }
        if (response.status === 409) {
          throw new Error('この組織は使用中のため削除できません')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error('Failed to delete municipal organization:', error)
      if (error.message.includes('見つかりません') || error.message.includes('削除できません')) {
        throw error
      }
      throw new Error('自治体組織の削除に失敗しました')
    }
  }
}

// =============================================================================
// 統合サービスインターフェース
// =============================================================================

// 環境に応じたAPIサービスを選択
const apiService = USE_MOCK_API ? mockApi : productionApi

if (USE_MOCK_API) {
  console.log('🔧 Using mock API for municipal organizations')
} else {
  console.log('🌐 Using production API for municipal organizations')
}

/**
 * 自治体組織一覧を取得
 * @returns {Promise<MunicipalOrganization[]>} 自治体組織のリスト
 */
export const getMunicipalOrganizations = async () => {
  try {
    const response = await apiService.getMunicipalOrganizations()

    // レスポンス形式を統一（作業方針 8.1 に準拠）
    if (Array.isArray(response)) {
      return response
    }

    return response.data || response
  } catch (error) {
    console.error('Service: Failed to get municipal organizations:', error)

    // エラーメッセージの改善
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('ネットワーク接続エラーが発生しました。インターネット接続を確認してください。')
    }

    throw error
  }
}

/**
 * 新しい自治体組織を作成
 * @param {MunicipalOrganizationRequest} organizationData - 組織データ
 * @returns {Promise<MunicipalOrganization>} 作成された組織データ
 */
export const createMunicipalOrganization = async (organizationData) => {
  // バリデーション
  if (!organizationData?.organizationName?.trim()) {
    throw new Error('組織名は必須です')
  }

  if (organizationData.organizationName.length > VALIDATION_RULES.ORGANIZATION_NAME_MAX_LENGTH) {
    throw new Error(`組織名は${VALIDATION_RULES.ORGANIZATION_NAME_MAX_LENGTH}文字以内で入力してください`)
  }

  if (organizationData.organizationName.length < VALIDATION_RULES.ORGANIZATION_NAME_MIN_LENGTH) {
    throw new Error('組織名は1文字以上で入力してください')
  }

  try {
    const cleanData = {
      organizationName: organizationData.organizationName.trim()
    }

    const response = await apiService.createMunicipalOrganization(cleanData)

    // レスポンス形式を統一
    return response.data || response
  } catch (error) {
    console.error('Service: Failed to create municipal organization:', error)
    throw error
  }
}

/**
 * 自治体組織を更新
 * @param {string|number} id - 組織ID
 * @param {MunicipalOrganizationRequest} organizationData - 更新する組織データ
 * @returns {Promise<MunicipalOrganization>} 更新された組織データ
 */
export const updateMunicipalOrganization = async (id, organizationData) => {
  // バリデーション
  if (!id) {
    throw new Error('組織IDが指定されていません')
  }

  if (!organizationData?.organizationName?.trim()) {
    throw new Error('組織名は必須です')
  }

  if (organizationData.organizationName.length > VALIDATION_RULES.ORGANIZATION_NAME_MAX_LENGTH) {
    throw new Error(`組織名は${VALIDATION_RULES.ORGANIZATION_NAME_MAX_LENGTH}文字以内で入力してください`)
  }

  if (organizationData.organizationName.length < VALIDATION_RULES.ORGANIZATION_NAME_MIN_LENGTH) {
    throw new Error('組織名は1文字以上で入力してください')
  }

  try {
    const cleanData = {
      organizationName: organizationData.organizationName.trim()
    }

    const response = await apiService.updateMunicipalOrganization(id, cleanData)

    // レスポンス形式を統一
    return response.data || response
  } catch (error) {
    console.error('Service: Failed to update municipal organization:', error)
    throw error
  }
}

/**
 * 自治体組織を削除
 * @param {string|number} id - 組織ID
 * @returns {Promise<MunicipalOrganization>} 削除された組織データ
 */
export const deleteMunicipalOrganization = async (id) => {
  // バリデーション
  if (!id) {
    throw new Error('組織IDが指定されていません')
  }

  try {
    const response = await apiService.deleteMunicipalOrganization(id)

    // レスポンス形式を統一
    return response.data || response
  } catch (error) {
    console.error('Service: Failed to delete municipal organization:', error)

    // エラーメッセージの改善
    if (error.message.includes('使用中')) {
      throw new Error('この組織は他の設定で使用されているため削除できません。先に関連する設定を変更してください。')
    }

    throw error
  }
}

/**
 * 開発用：モックデータをリセット
 * 本番環境では何もしない
 */
export const resetMockData = () => {
  if (USE_MOCK_API) {
    mockOrganizations = [
      {
        id: 1,
        organizationName: '廃棄物管理課',
        createdAt: '2024-01-15T09:00:00.000Z',
        updatedAt: '2024-01-15T09:00:00.000Z'
      },
      {
        id: 2,
        organizationName: '清掃事業所',
        createdAt: '2024-01-20T10:30:00.000Z',
        updatedAt: '2024-01-20T10:30:00.000Z'
      },
      {
        id: 3,
        organizationName: '環境政策課',
        createdAt: '2024-02-01T14:15:00.000Z',
        updatedAt: '2024-02-01T14:15:00.000Z'
      },
      {
        id: 4,
        organizationName: '環境衛生課',
        createdAt: '2024-02-10T16:45:00.000Z',
        updatedAt: '2024-02-10T16:45:00.000Z'
      },
      {
        id: 5,
        organizationName: 'リサイクル推進課',
        createdAt: '2024-03-01T11:20:00.000Z',
        updatedAt: '2024-03-01T11:20:00.000Z'
      }
    ]
    console.log('Mock data has been reset')
  }
}

/**
 * 組織名のバリデーション
 * @param {string} organizationName - 検証する組織名
 * @returns {string|null} エラーメッセージまたはnull（正常）
 */
export const validateOrganizationName = (organizationName) => {
  if (!organizationName || !organizationName.trim()) {
    return '組織名は必須です'
  }

  const trimmedName = organizationName.trim()

  if (trimmedName.length < VALIDATION_RULES.ORGANIZATION_NAME_MIN_LENGTH) {
    return '組織名は1文字以上で入力してください'
  }

  if (trimmedName.length > VALIDATION_RULES.ORGANIZATION_NAME_MAX_LENGTH) {
    return `組織名は${VALIDATION_RULES.ORGANIZATION_NAME_MAX_LENGTH}文字以内で入力してください`
  }

  // 特殊文字チェック（必要に応じて）
  const invalidChars = /[<>'"&]/
  if (invalidChars.test(trimmedName)) {
    return '組織名に使用できない文字が含まれています'
  }

  return null
}

/**
 * 重複チェック用ヘルパー（フロントエンド用）
 * @param {string} organizationName - チェックする組織名
 * @param {MunicipalOrganization[]} existingOrganizations - 既存の組織リスト
 * @param {number|null} excludeId - 除外する組織ID（編集時）
 * @returns {boolean} 重複している場合true
 */
export const isDuplicateOrganizationName = (organizationName, existingOrganizations, excludeId = null) => {
  if (!organizationName || !Array.isArray(existingOrganizations)) {
    return false
  }

  const trimmedName = organizationName.trim().toLowerCase()

  return existingOrganizations.some(org =>
    org.id !== excludeId &&
    org.organizationName.toLowerCase() === trimmedName
  )
}

/**
 * 現在使用中のAPIタイプを取得
 * @returns {string} 'mock' または 'production'
 */
export const getApiType = () => {
  return USE_MOCK_API ? 'mock' : 'production'
}

/**
 * バリデーションルールを取得
 * @returns {Object} バリデーションルール定数
 */
export const getValidationRules = () => {
  return { ...VALIDATION_RULES }
}

export default {
  getMunicipalOrganizations,
  createMunicipalOrganization,
  updateMunicipalOrganization,
  deleteMunicipalOrganization,
  validateOrganizationName,
  isDuplicateOrganizationName,
  resetMockData,
  getApiType,
  getValidationRules
}
